import { useCallback, useEffect, useRef, useState } from 'react';

/*
 * Кадрирование фотографии по кругу — как в мессенджерах: снимок двигают
 * пальцем или мышью, масштаб меняют ползунком или колесом. Наружу уходит
 * готовый квадрат, обрезанный браузером, — сервер получает маленький файл
 * и ничего не пересчитывает.
 */

const OUTPUT = 320; // сторона готовой картинки, пикселей
const VIEW = 260; // размер круга на экране

export function AvatarCropper({
  file,
  onCancel,
  onReady,
}: {
  file: File;
  onCancel: () => void;
  onReady: (blob: Blob) => void;
}) {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const dragRef = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setImage(img);
      setScale(1);
      setOffset({ x: 0, y: 0 });
    };
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // масштаб 1 = картинка вписана в круг по короткой стороне
  const baseScale = image ? VIEW / Math.min(image.width, image.height) : 1;
  const drawW = image ? image.width * baseScale * scale : 0;
  const drawH = image ? image.height * baseScale * scale : 0;

  /** Не даём вытащить картинку за пределы круга. */
  const clamp = useCallback(
    (next: { x: number; y: number }) => {
      const limitX = Math.max(0, (drawW - VIEW) / 2);
      const limitY = Math.max(0, (drawH - VIEW) / 2);
      return {
        x: Math.min(limitX, Math.max(-limitX, next.x)),
        y: Math.min(limitY, Math.max(-limitY, next.y)),
      };
    },
    [drawW, drawH],
  );

  useEffect(() => setOffset((cur) => clamp(cur)), [clamp]);

  const startDrag = (e: React.PointerEvent) => {
    dragRef.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
    const move = (ev: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      setOffset(clamp({ x: d.ox + (ev.clientX - d.x), y: d.oy + (ev.clientY - d.y) }));
    };
    const up = () => {
      dragRef.current = null;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const save = () => {
    if (!image) return;
    setBusy(true);
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT;
    canvas.height = OUTPUT;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // экранный круг переносим в готовый квадрат один к одному
    const k = OUTPUT / VIEW;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, OUTPUT, OUTPUT);
    ctx.drawImage(
      image,
      (OUTPUT - drawW * k) / 2 + offset.x * k,
      (OUTPUT - drawH * k) / 2 + offset.y * k,
      drawW * k,
      drawH * k,
    );
    canvas.toBlob(
      (blob) => {
        setBusy(false);
        if (blob) onReady(blob);
      },
      'image/jpeg',
      0.9,
    );
  };

  return (
    <div className="backdrop confirm" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="dialog crop-card" role="dialog" aria-modal="true">
        <div className="dialog-body form">
        <h2>Фотография профиля</h2>
        <p>Перетащите снимок и подберите масштаб: в круг попадёт то, что увидят коллеги.</p>

        <div
          className="crop-stage"
          style={{ width: VIEW, height: VIEW }}
          onPointerDown={startDrag}
          onWheel={(e) => setScale((s) => Math.min(4, Math.max(1, s - e.deltaY * 0.002)))}
        >
          {image && (
            <img
              src={image.src}
              alt=""
              draggable={false}
              // положение задаётся в пикселях один в один с расчётом для canvas:
              // процентные отступы браузер считает от контейнера, и превью
              // тогда расходится с результатом
              style={{
                width: drawW,
                height: drawH,
                left: (VIEW - drawW) / 2 + offset.x,
                top: (VIEW - drawH) / 2 + offset.y,
              }}
            />
          )}
          <div className="crop-mask" />
        </div>

        <input
          className="crop-zoom"
          type="range"
          min={1}
          max={4}
          step={0.01}
          value={scale}
          onChange={(e) => setScale(Number(e.target.value))}
          aria-label="Масштаб"
        />

        </div>
        <div className="dialog-foot">
          <button className="btn outline" onClick={onCancel}>
            Отмена
          </button>
          <button className="btn primary" disabled={!image || busy} onClick={save}>
            {busy ? 'Сохраняю…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
}
